import React from 'react';
import { Box, Paper, Typography, Tooltip } from '@mui/material';

/**
 * Faixa de indicadores acima da tabela.
 *
 * O problema que ela resolve: a aba Segurança da TCHE tem 428 linhas, e a
 * pergunta que o operador faz não é "quais são as 428" — é "quantos estão com
 * telnet aberto". A tabela obriga a ler tudo pra descobrir; o indicador
 * responde na hora.
 *
 * Cada card com `filtro` é clicável e filtra a tabela nas linhas que ele
 * conta. Sem isso o contador vira enfeite: você sabe que são 379 sem syslog e
 * continua sem saber quais.
 *
 * Duas linhas e rolagem vertical, não rolagem horizontal: card que sai pela
 * direita fica escondido sem nenhuma pista de que existe — e é justamente onde
 * caem os últimos indicadores da lista, que costumam ser os de BGP.
 */

// Paleta clara, a mesma do toolbar da página. O painel antes era escuro e
// destoava do resto do sistema.
const TONS = {
  bad:  { fg: '#b91c1c', border: '#fca5a5', bg: '#fef2f2', sel: '#fee2e2' },
  warn: { fg: '#b45309', border: '#fcd34d', bg: '#fffbeb', sel: '#fef3c7' },
  ok:   { fg: '#15803d', border: '#86efac', bg: '#f0fdf4', sel: '#dcfce7' },
  info: { fg: '#1d4ed8', border: '#bfdbfe', bg: '#eff6ff', sel: '#dbeafe' },
};

const mesmoFiltro = (a, b) => {
  if (!a || !b) return false;
  if (typeof a === 'string' || typeof b === 'string') return a === b;
  return a.col === b.col && a.op === b.op && a.value === b.value;
};

export default function TableSummary({ summary, filtroAtivo, onFiltrar }) {
  if (!summary || !summary.length) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignContent: 'flex-start',
        gap: 1,
        px: 1,
        pt: 1,
        pb: 1,
        bgcolor: '#f8fafc',
        borderBottom: '1px solid #e2e8f0',
        // ~2 fileiras de card; o que passar disso rola
        maxHeight: 132,
        overflowY: 'auto',
        flexShrink: 0,
        '&::-webkit-scrollbar': { width: 8 },
        '&::-webkit-scrollbar-thumb': { bgcolor: '#cbd5e1', borderRadius: 4 },
      }}
    >
      {summary.map((k, i) => {
        const t = TONS[k.tone] || TONS.info;
        const clicavel = !!k.filtro && !!onFiltrar;
        const ativo = clicavel && mesmoFiltro(filtroAtivo, k.filtro);
        // "0 de 229" com tom ok é a ausência do problema: não disputa atenção
        const apagado = k.tone === 'ok' && k.value === 0;
        const card = (
          <Paper
            elevation={0}
            onClick={clicavel ? () => onFiltrar(ativo ? null : k.filtro) : undefined}
            sx={{
              px: 1.5,
              py: 0.75,
              minWidth: 116,
              flexShrink: 0,
              bgcolor: ativo ? t.sel : t.bg,
              border: 1,
              borderColor: ativo ? t.fg : t.border,
              borderWidth: ativo ? 2 : 1,
              borderRadius: 1,
              opacity: apagado ? 0.5 : 1,
              cursor: clicavel ? 'pointer' : 'default',
              transition: 'border-color .12s, background-color .12s',
              '&:hover': clicavel ? { borderColor: t.fg, bgcolor: t.sel } : undefined,
            }}
          >
            <Typography
              sx={{
                fontSize: 21,
                lineHeight: 1.15,
                fontWeight: 700,
                color: t.fg,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {k.value}
              {k.of != null && (
                <Typography
                  component="span"
                  sx={{ fontSize: 12, fontWeight: 500, color: '#94a3b8', ml: 0.5 }}
                >
                  /{k.of}
                </Typography>
              )}
            </Typography>
            <Typography sx={{ fontSize: 11, color: '#475569', whiteSpace: 'nowrap' }}>
              {k.label}
            </Typography>
          </Paper>
        );
        // O hint carrega o porquê: "sem uRPF" não diz nada pra quem não é de
        // rede; "não barra tráfego com origem forjada" diz.
        return k.hint ? (
          <Tooltip key={i} title={k.hint} placement="bottom">
            {card}
          </Tooltip>
        ) : (
          <React.Fragment key={i}>{card}</React.Fragment>
        );
      })}
    </Box>
  );
}
